import React from "react";
import { Link, useNavigate } from "react-router-dom";

import SEO from "../../seo/SEO";

import {
  LayoutDashboard,
  ArrowRight,
  CheckCircle2,
  BarChart3,
  Database,
  ShieldCheck,
  Cloud,
  Boxes,
  Workflow,
  MonitorSmartphone,
  Puzzle,
  Layers3,
} from "lucide-react";

import "./CustomDashboardDevelopment.css";


/* =========================================================
   DASHBOARD SERVICES
========================================================= */

const dashboardServices = [
  {
    icon: <LayoutDashboard size={28} />,
    title: "Custom Admin Dashboards",
    description:
      "Custom business dashboards designed for realtime monitoring, KPI tracking, operational visibility and management workflows.",
  },

  {
    icon: <Workflow size={28} />,
    title: "Workflow Automation Systems",
    description:
      "Automated business workflows with modular architecture, task orchestration, approvals, triggers and realtime operational processes.",
  },

  {
    icon: <Boxes size={28} />,
    title: "Modular Dashboard Systems",
    description:
      "Scalable dashboard platforms with reusable modules for CRM, ERP, MIS, analytics, finance, operations and business management.",
  },

  {
    icon: <BarChart3 size={28} />,
    title: "Analytics & MIS Dashboards",
    description:
      "Interactive analytics dashboards with realtime charts, business KPIs, management reports and intelligent data visualization.",
  },

  {
    icon: <Database size={28} />,
    title: "Realtime Data Integration",
    description:
      "Connect dashboards with databases, APIs and business systems to provide realtime operational and management information.",
  },

  {
    icon: <MonitorSmartphone size={28} />,
    title: "Responsive Dashboard Systems",
    description:
      "Cross-device dashboard experiences optimized for desktop, tablet and mobile business accessibility.",
  },

  {
    icon: <Puzzle size={28} />,
    title: "Third-Party Integrations",
    description:
      "Integrate dashboards with payment systems, APIs, CRM, ERP, ecommerce platforms, analytics tools and cloud services.",
  },

  {
    icon: <ShieldCheck size={28} />,
    title: "Role-Based Security",
    description:
      "Secure authentication, role-based access control, protected APIs and controlled access to business information.",
  },
];


/* =========================================================
   TECHNOLOGY STACK
========================================================= */

const technologies = [
  "React.js",
  "Next.js",
  "TypeScript",
  "Tailwind CSS",
  "FastAPI",
  "Node.js",
  "PostgreSQL",
  "MongoDB",
  "Redis",
  "Docker",
  "Kubernetes",
  "AWS",
  "Firebase",
  "Socket.IO",
  "Recharts",
  "Microservices",
];


/* =========================================================
   DASHBOARD EXAMPLES
========================================================= */

const dashboardShots = [
  {
    title: "Realtime Analytics Dashboard",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=1400&auto=format&fit=crop",
  },

  {
    title: "Enterprise KPI Monitoring",
    image:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=1400&auto=format&fit=crop",
  },

  {
    title: "Modern Modular Admin System",
    image:
      "https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=1400&auto=format&fit=crop",
  },
];


/* =========================================================
   CUSTOM DASHBOARD DEVELOPMENT PAGE
========================================================= */

export default function CustomDashboardDevelopment() {

  const navigate = useNavigate();


  /* =======================================================
     NAVIGATION
  ======================================================= */

  const handleConsultation = () => {
    navigate("/contact?service=dashboard");
  };


  /* =======================================================
     CANONICAL URL
  ======================================================= */

  const canonicalUrl =
    "https://www.bairagi-consultants.com/custom-dashboard-development";


  /* =======================================================
     SERVICE STRUCTURED DATA
  ======================================================= */

  const serviceSchema = {
    "@context": "https://schema.org",

    "@type": "Service",

    name: "Custom Dashboard Development Services",

    serviceType:
      "Custom Business Dashboard and MIS Development",

    url: canonicalUrl,

    description:
      "Custom dashboard development for MIS reporting, realtime analytics, KPI monitoring, admin panels, operational dashboards, workflow automation and business intelligence systems.",

    provider: {
      "@type": "Organization",
      name: "Bairagi Consultants",
      url: "https://www.bairagi-consultants.com",
    },

    areaServed: {
      "@type": "Country",
      name: "India",
    },
  };


  return (
    <>

      {/* =====================================================
          SEO
      ====================================================== */}

      <SEO
        title="Custom MIS Dashboard Development Company | Bairagi Consultants"
        description="Custom MIS and business dashboard development for realtime analytics, KPI tracking, reporting, workflow automation, admin panels and operational management systems."
        path="/custom-dashboard-development"
        schema={serviceSchema}
      />


      <div className="dashboard-page">

        {/* =================================================
            HERO
        ================================================= */}

        <section className="dashboard-hero dashboard-container">

          <div className="dashboard-hero-content">

            <span className="dashboard-badge">
              Custom Dashboard & MIS Development
            </span>


            <h1 className="dashboard-title">

              Scalable

              <span>
                {" "}Dashboard Systems
              </span>

              <br />

              Built For Modern Businesses

            </h1>


            <p className="dashboard-description">

              We build custom MIS and business
              dashboard systems with realtime
              analytics, KPI monitoring, workflow
              automation, modular integrations and
              scalable backend architecture.

            </p>


            {/* HERO ACTIONS */}

            <div className="dashboard-actions">

              <button
                type="button"
                className="dashboard-primary-btn"
                onClick={handleConsultation}
              >

                Book Free Consultation

                <ArrowRight size={18} />

              </button>


              <Link
                to="/case-studies"
                className="dashboard-secondary-btn"
              >

                Explore Dashboard Solutions

              </Link>

            </div>

          </div>


          <div className="dashboard-hero-card">

            <img
              src="https://images.unsplash.com/photo-1551434678-e076c223a692?q=80&w=1400&auto=format&fit=crop"
              alt="Custom MIS dashboard and business analytics development"
            />

          </div>

        </section>


        {/* =================================================
            SERVICES
        ================================================= */}

        <section className="dashboard-section dashboard-container">

          <div className="dashboard-section-header">

            <h2>
              Custom Dashboard
              <br />
              Development Services
            </h2>


            <p>

              Business dashboards designed for
              operational visibility, realtime
              reporting, management analytics,
              workflow automation and scalable
              software integration.

            </p>

          </div>


          <div className="dashboard-services-grid">

            {dashboardServices.map((service, index) => (

              <div
                key={index}
                className="dashboard-service-card"
              >

                {service.icon}

                <h3>
                  {service.title}
                </h3>

                <p>
                  {service.description}
                </p>

              </div>

            ))}

          </div>

        </section>


        {/* =================================================
            MIS & BUSINESS REPORTING
        ================================================= */}

        <section className="dashboard-section dashboard-container">

          <div className="dashboard-section-header">

            <h2>
              MIS Dashboard
              <br />
              Development
            </h2>


            <p>

              Management Information System dashboards
              help decision-makers monitor operational
              data, KPIs, sales, finance, customers
              and business performance from one
              centralized platform.

            </p>

          </div>


          <div className="dashboard-services-grid">

            <div className="dashboard-service-card">

              <BarChart3 size={28} />

              <h3>
                KPI Monitoring
              </h3>

              <p>
                Track important business metrics,
                targets and performance indicators
                through clear management dashboards.
              </p>

            </div>


            <div className="dashboard-service-card">

              <Database size={28} />

              <h3>
                Centralized Business Data
              </h3>

              <p>
                Combine information from different
                databases and applications to create
                consolidated management reporting.
              </p>

            </div>


            <div className="dashboard-service-card">

              <LayoutDashboard size={28} />

              <h3>
                Management Reporting
              </h3>

              <p>
                Provide management teams with
                structured operational reports,
                trends, comparisons and business
                performance information.
              </p>

            </div>

          </div>

        </section>


        {/* =================================================
            DASHBOARD EXAMPLES
        ================================================= */}

        <section className="dashboard-section dashboard-container">

          <div className="dashboard-section-header">

            <h2>
              Modern Dashboard
              <br />
              Experiences
            </h2>


            <p>

              Dashboard interfaces designed for
              clear data presentation, operational
              visibility, management reporting and
              efficient business workflows.

            </p>

          </div>


          <div className="dashboard-shots-grid">

            {dashboardShots.map((shot, index) => (

              <div
                key={index}
                className="dashboard-shot-card"
              >

                <img
                  src={shot.image}
                  alt={shot.title}
                />


                <div className="dashboard-shot-overlay">

                  <h3>
                    {shot.title}
                  </h3>

                </div>

              </div>

            ))}

          </div>

        </section>


        {/* =================================================
            BUSINESS SYSTEM INTEGRATION
        ================================================= */}

        <section className="dashboard-section dashboard-container">

          <div className="dashboard-section-header">

            <h2>
              Dashboard Integration
              <br />
              With Business Systems
            </h2>


            <p>

              Custom dashboards can connect with
              CRM, ERP, ecommerce, mobile applications,
              finance systems and other operational
              software to create one centralized
              management environment.

            </p>

          </div>


          <div className="dashboard-services-grid">

            <div className="dashboard-service-card">

              <Puzzle size={28} />

              <h3>
                CRM Integration
              </h3>

              <p>
                Track leads, customers, sales
                pipelines and CRM activity through
                centralized dashboards.
              </p>

            </div>


            <div className="dashboard-service-card">

              <Boxes size={28} />

              <h3>
                ERP & Operations
              </h3>

              <p>
                Connect operational modules,
                inventory, finance and business
                processes with management dashboards.
              </p>

            </div>


            <div className="dashboard-service-card">

              <Workflow size={28} />

              <h3>
                Workflow Automation
              </h3>

              <p>
                Manage tasks, approvals,
                notifications and operational
                workflows through integrated
                dashboard systems.
              </p>

            </div>

          </div>

        </section>


        {/* =================================================
            TECHNOLOGY STACK
        ================================================= */}

        <section className="dashboard-section dashboard-container">

          <div className="dashboard-section-header">

            <h2>
              Dashboard Development
              <br />
              Technology Stack
            </h2>


            <p>

              Modern frontend, backend, database
              and cloud technologies for building
              scalable dashboards and management
              information systems.

            </p>

          </div>


          <div className="dashboard-tech-stack">

            {technologies.map((tech) => (

              <span
                key={tech}
                className="dashboard-tech-tag"
              >

                {tech}

              </span>

            ))}

          </div>

        </section>


        {/* =================================================
            FEATURED PLATFORM
        ================================================= */}

        <section className="dashboard-section dashboard-container">

          <div className="dashboard-case-study">

            <span>
              Business Dashboard Platform
            </span>


            <h2>
              Enterprise Operations
              Intelligence Platform
            </h2>


            <p>

              A centralized operations dashboard
              can combine realtime analytics,
              operational monitoring, CRM data,
              financial information, reporting
              and workflow automation within one
              modular business platform.

            </p>


            <Link
              to="/case-studies"
              className="dashboard-secondary-btn"
            >

              Explore Case Studies

              <ArrowRight size={18} />

            </Link>

          </div>

        </section>


        {/* =================================================
            WHY CHOOSE US
        ================================================= */}

        <section className="dashboard-section dashboard-container">

          <div className="dashboard-section-header">

            <h2>
              Why Choose Bairagi Consultants
              <br />
              for Dashboard Development?
            </h2>

          </div>


          <div className="dashboard-services-grid">

            <div className="dashboard-service-card">

              <CheckCircle2 size={28} />

              <h3>
                Business-Focused Design
              </h3>

              <p>

                Dashboards are designed around
                your actual business processes,
                reporting requirements and
                management information needs.

              </p>

            </div>


            <div className="dashboard-service-card">

              <Layers3 size={28} />

              <h3>
                Modular Architecture
              </h3>

              <p>

                Reusable dashboard components
                allow new modules, workflows
                and integrations to be added
                as requirements grow.

              </p>

            </div>


            <div className="dashboard-service-card">

              <Cloud size={28} />

              <h3>
                Scalable Backend Systems
              </h3>

              <p>

                Dashboard platforms can use
                scalable APIs, secure databases
                and cloud-ready infrastructure
                for reliable business operations.

              </p>

            </div>

          </div>

        </section>


        {/* =================================================
            SECURITY
        ================================================= */}

        <section className="dashboard-section dashboard-container">

          <div className="dashboard-section-header">

            <h2>
              Secure Business
              <br />
              Dashboard Architecture
            </h2>


            <p>

              Business dashboards often contain
              sensitive operational information.
              We design systems with authentication,
              role-based permissions and secure
              backend access controls.

            </p>

          </div>


          <div className="dashboard-services-grid">

            <div className="dashboard-service-card">

              <ShieldCheck size={28} />

              <h3>
                Role-Based Access
              </h3>

              <p>
                Different dashboard features and
                information can be controlled
                according to user roles and
                responsibilities.
              </p>

            </div>


            <div className="dashboard-service-card">

              <Database size={28} />

              <h3>
                Secure Data Access
              </h3>

              <p>
                Backend APIs and database access
                can be protected through secure
                authentication and authorization.
              </p>

            </div>


            <div className="dashboard-service-card">

              <Cloud size={28} />

              <h3>
                Cloud Deployment
              </h3>

              <p>
                Dashboard applications can be
                deployed on suitable cloud
                infrastructure with scalable
                backend services.
              </p>

            </div>

          </div>

        </section>


        {/* =================================================
            FINAL CTA
        ================================================= */}

        <section className="dashboard-section dashboard-container">

          <div className="dashboard-cta">

            <h2>
              Ready To Build Your
              <br />
              Custom Dashboard Platform?
            </h2>


            <p>

              Let's discuss your reporting,
              KPI monitoring, MIS, operational
              workflows and integration requirements
              to design the right dashboard system
              for your business.

            </p>


            <button
              type="button"
              onClick={handleConsultation}
            >

              Schedule Consultation

              <ArrowRight size={18} />

            </button>

          </div>

        </section>

      </div>

    </>
  );
}